<template>
  <div class="min-h-full bg-casino-dark">
    <div class="p-3 sm:p-4 lg:p-6 space-y-6 sm:space-y-8">
      <div v-if="showHomeSkeleton" class="space-y-6 sm:space-y-8">
        <div class="rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:p-4 space-y-3">
          <SkeletonBlock custom-class="h-44 sm:h-56 lg:h-72 w-full rounded-xl" />
          <div class="flex items-center gap-2">
            <SkeletonBlock v-for="idx in 3" :key="`hero-dot-${idx}`" custom-class="h-2 w-8 rounded-full" />
          </div>
        </div>

        <div class="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
          <SkeletonBlock custom-class="h-4 w-48" />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SkeletonBlock v-for="idx in 2" :key="`announcement-skeleton-${idx}`" custom-class="h-16 w-full rounded-lg" />
          </div>
        </div>

        <div v-for="idx in 2" :key="`rail-skeleton-${idx}`" class="space-y-3">
          <div class="flex items-center justify-between">
            <SkeletonBlock custom-class="h-6 w-44" />
            <SkeletonBlock custom-class="h-4 w-20" />
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
            <div v-for="cardIdx in 5" :key="`rail-card-skeleton-${idx}-${cardIdx}`" class="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
              <SkeletonBlock custom-class="h-28 w-full rounded-lg" />
              <SkeletonBlock custom-class="h-4 w-3/4" />
              <SkeletonBlock custom-class="h-3 w-1/2" />
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="cmsStore.error" class="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-red-200">
        {{ cmsStore.error }}
      </div>

      <template v-else>
        <section v-if="heroBanners.length" class="space-y-3">
          <div class="relative rounded-2xl border border-white/10 overflow-hidden bg-casino-card">
            <button
              type="button"
              class="block w-full text-left"
              @click="openBannerLink(activeHeroBanner)"
            >
              <picture>
                <source v-if="activeHeroBanner.imageMobile" media="(max-width: 768px)" :srcset="activeHeroBanner.imageMobile" />
                <img
                  :src="heroImage(activeHeroBanner)"
                  :alt="tCms(activeHeroBanner.title)"
                  class="w-full h-44 sm:h-56 lg:h-[360px] object-cover"
                  loading="eager"
                />
              </picture>
            </button>

            <div class="absolute inset-0 bg-black/35 pointer-events-none" />

            <div class="absolute inset-0 flex items-end pointer-events-none">
              <div class="p-4 sm:p-6 lg:p-8 max-w-2xl space-y-2">
                <p class="text-xs sm:text-sm uppercase tracking-[0.14em] text-white/70">{{ t('home.featuredCampaign') }}</p>
                <h1 class="text-xl sm:text-2xl lg:text-4xl font-bold text-white leading-tight">{{ tCms(activeHeroBanner.title) }}</h1>
                <p v-if="activeHeroBanner.subtitle" class="text-sm sm:text-base text-white/85">{{ tCms(activeHeroBanner.subtitle) }}</p>
              </div>
            </div>

            <div class="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 z-10">
              <button
                type="button"
                class="h-9 w-9 rounded-full border border-white/25 bg-black/35 text-white hover:bg-black/55 transition"
                :aria-label="t('home.prevBanner')"
                @click="prevHero"
              >
                <ChevronLeft class="w-4 h-4 mx-auto" />
              </button>
              <button
                type="button"
                class="h-9 w-9 rounded-full border border-white/25 bg-black/35 text-white hover:bg-black/55 transition"
                :aria-label="t('home.nextBanner')"
                @click="nextHero"
              >
                <ChevronRight class="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <button
                v-for="(banner, idx) in heroBanners"
                :key="banner.id || idx"
                type="button"
                :aria-label="`Ir al banner ${idx + 1}`"
                @click="goToHero(idx)"
                :class="[
                  'h-2 rounded-full transition-all',
                  heroIndex === idx ? 'w-8 bg-casino-primary' : 'w-3 bg-white/25 hover:bg-white/40'
                ]"
              />
            </div>
            <button
              type="button"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs sm:text-sm text-white hover:bg-white/10 transition"
              @click="openBannerLink(activeHeroBanner)"
            >
              {{ tCms(activeHeroBanner.ctaText) || t('cms.seeMore') }}
              <ArrowUpRight class="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        <section v-if="featuredLobbyRail" class="space-y-3">
          <div class="flex items-center justify-between gap-4">
            <div>
              <h2 class="text-base sm:text-lg font-bold text-white">{{ tCms(featuredLobbyRail.title) || t('home.lobby') }}</h2>
              <p v-if="featuredLobbyRail.subtitle" class="text-xs sm:text-sm text-gray-400 mt-1">{{ tCms(featuredLobbyRail.subtitle) }}</p>
            </div>
            <RouterLink
              :to="`/lobby?lobby=${featuredLobbyRail.code}`"
              class="inline-flex items-center gap-1 text-xs sm:text-sm text-casino-primary hover:text-casino-primary-light"
            >
              {{ t('home.viewLobby') }}
              <ArrowUpRight class="w-3.5 h-3.5" />
            </RouterLink>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
            <GameCard
              v-for="game in featuredLobbyRail.previewGames"
              :key="`${featuredLobbyRail.code}-${game.id}`"
              :game="game"
              @play="handlePlay"
              @login-required="handleLoginRequired"
            />
          </div>
        </section>

        <section v-if="announcementItems.length || isAnnouncementLoading" class="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
          <div class="flex items-center justify-between gap-3 mb-3">
            <h2 class="text-sm sm:text-base font-semibold text-white">{{ t('home.announcements') }}</h2>
            <div class="flex items-center gap-2">
              <span class="text-xs text-gray-400">{{ t('home.activeCount', { count: announcementItems.length }) }}</span>
              <button
                v-if="announcementItems.length > 1"
                type="button"
                class="h-7 w-7 rounded-full border border-white/20 bg-white/5 text-white hover:bg-white/10 transition"
                :aria-label="t('home.prevAnnouncement')"
                @click="scrollAnnouncements(-1)"
              >
                <ChevronLeft class="w-3.5 h-3.5 mx-auto" />
              </button>
              <button
                v-if="announcementItems.length > 1"
                type="button"
                class="h-7 w-7 rounded-full border border-white/20 bg-white/5 text-white hover:bg-white/10 transition"
                :aria-label="t('home.nextAnnouncement')"
                @click="scrollAnnouncements(1)"
              >
                <ChevronRight class="w-3.5 h-3.5 mx-auto" />
              </button>
            </div>
          </div>

          <div v-if="isAnnouncementLoading" class="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
            <div
              v-for="idx in 3"
              :key="`announcement-loading-${idx}`"
              class="min-w-[280px] sm:min-w-[360px] rounded-lg border border-white/10 bg-casino-card/60 p-3 sm:p-4 space-y-2"
            >
              <SkeletonBlock custom-class="h-3 w-24" />
              <SkeletonBlock custom-class="h-4 w-3/4" />
              <SkeletonBlock custom-class="h-3 w-full" />
              <SkeletonBlock custom-class="h-3 w-4/5" />
            </div>
          </div>

          <div
            v-else
            ref="announcementScroller"
            class="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-1"
          >
            <article
              v-for="item in announcementItems"
              :key="item.id"
              class="min-w-[280px] sm:min-w-[420px] rounded-lg border border-white/10 bg-casino-card/60 p-3 sm:p-4 flex items-start gap-3 snap-start"
            >
              <img
                v-if="item.image"
                :src="item.image"
                :alt="tCms(item.title)"
                class="w-14 h-14 rounded-md object-cover flex-shrink-0"
              />
              <div class="min-w-0 space-y-1">
                <p class="text-xs text-gray-400">{{ formatAnnouncementDate(item) }}</p>
                <h3 class="text-sm font-semibold text-white truncate">{{ tCms(item.title) }}</h3>
                <p class="text-xs sm:text-sm text-gray-300 line-clamp-2">{{ tCms(item.description) }}</p>
              </div>
            </article>
          </div>
        </section>

        <section class="space-y-3">
          <div class="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <button
              v-for="category in categories"
              :key="category.id"
              @click="activeCategory = category.id"
              :class="[
                'px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors',
                activeCategory === category.id
                  ? 'bg-white text-black'
                  : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
              ]"
            >
              {{ category.label }}
            </button>
          </div>
        </section>

        <section
          v-for="lobby in remainingLobbyRails"
          :key="lobby.code"
          class="space-y-3"
        >
          <div class="flex items-center justify-between gap-4">
            <div>
              <h2 class="text-base sm:text-lg font-bold text-white">{{ tCms(lobby.title) || t('home.lobby') }}</h2>
              <p v-if="lobby.subtitle" class="text-xs sm:text-sm text-gray-400 mt-1">{{ tCms(lobby.subtitle) }}</p>
            </div>
            <RouterLink
              :to="`/lobby?lobby=${lobby.code}`"
              class="inline-flex items-center gap-1 text-xs sm:text-sm text-casino-primary hover:text-casino-primary-light"
            >
              {{ t('home.viewLobby') }}
              <ArrowUpRight class="w-3.5 h-3.5" />
            </RouterLink>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
            <GameCard
              v-for="game in lobby.previewGames"
              :key="`${lobby.code}-${game.id}`"
              :game="game"
              @play="handlePlay"
              @login-required="handleLoginRequired"
            />
          </div>
        </section>

        <section
          v-if="pendingRemainingLobbyCount > 0"
          class="space-y-3"
        >
          <div
            v-for="idx in pendingRemainingLobbyCount"
            :key="`pending-lobby-${idx}`"
            class="space-y-3"
          >
            <div class="flex items-center justify-between">
              <SkeletonBlock custom-class="h-5 w-44" />
              <SkeletonBlock custom-class="h-4 w-20" />
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
              <div
                v-for="cardIdx in 5"
                :key="`pending-lobby-${idx}-card-${cardIdx}`"
                class="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2"
              >
                <SkeletonBlock custom-class="h-28 w-full rounded-lg" />
                <SkeletonBlock custom-class="h-4 w-3/4" />
                <SkeletonBlock custom-class="h-3 w-1/2" />
              </div>
            </div>
          </div>
        </section>

        <section
          v-for="carousel in promoCarousels"
          :key="carousel.key"
          class="space-y-3"
        >
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-base sm:text-lg font-bold text-white">{{ tCms(carousel.title) || t('cms.promotions') }}</h2>
            <span class="text-xs text-gray-400">{{ t('home.bannersCount', { count: carousel.items.length }) }}</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            <a
              v-for="banner in carousel.items"
              :key="banner.id"
              :href="banner.link || '#'"
              :target="banner.target || '_self'"
              rel="noopener noreferrer"
              class="group relative overflow-hidden rounded-xl border border-white/10 bg-casino-card"
              @click.prevent="openBannerLink(banner)"
            >
              <picture>
                <source v-if="banner.imageMobile" media="(max-width: 768px)" :srcset="banner.imageMobile" />
                <img
                  :src="heroImage(banner)"
                  :alt="tCms(banner.title)"
                  class="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
              </picture>
              <div class="absolute inset-0 bg-black/35" />
              <div class="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                <h3 class="text-white font-semibold text-sm sm:text-base">{{ tCms(banner.title) }}</h3>
                <p v-if="banner.subtitle" class="text-white/85 text-xs sm:text-sm mt-1">{{ tCms(banner.subtitle) }}</p>
              </div>
            </a>
          </div>
        </section>

        <section v-if="pendingPromoCarouselCount > 0" class="space-y-3">
          <div class="flex items-center justify-between gap-3">
            <SkeletonBlock custom-class="h-5 w-48" />
            <SkeletonBlock custom-class="h-4 w-20" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            <div
              v-for="idx in Math.min(3, pendingPromoCarouselCount + 1)"
              :key="`promo-pending-${idx}`"
              class="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2"
            >
              <SkeletonBlock custom-class="h-40 w-full rounded-lg" />
              <SkeletonBlock custom-class="h-4 w-2/3" />
              <SkeletonBlock custom-class="h-3 w-1/2" />
            </div>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-vue-next';
import GameCard from '@/components/GameCard.vue';
import SkeletonBlock from '@/components/SkeletonBlock.vue';
import { APP_CONFIG } from '@/config/app';
import { getOperatorCmsConfig } from '@/config/cms-containers';
import { useAuthStore } from '@/stores/auth';
import { useCmsContentStore } from '@/stores/cmsContent';
import { currentLanguageTag, tCms } from '@/i18n';

const { t } = useI18n();
const router = useRouter();
const openLogin = inject('openLogin', null);
const authStore = useAuthStore();
const cmsStore = useCmsContentStore();

const activeCategory = ref('all');
const heroIndex = ref(0);
const announcementScroller = ref(null);
const heroAutoplayIntervalMs = 6000;
let heroAutoplayTimer = null;

const placeholderImage = APP_CONFIG.gamePlaceholderImage;

const categories = computed(() => [
  { id: 'all', label: t('search.categories.all') },
  { id: 'slot', label: t('sidebar.slots') },
  { id: 'table', label: t('sidebar.tableGames') },
  { id: 'live', label: t('sidebar.liveCasino') },
  { id: 'featured', label: t('search.categories.featured') },
]);

const showHomeSkeleton = computed(() => !cmsStore.initialized || (cmsStore.loading && !cmsStore.loaded));

const heroBanners = computed(() => {
  const hero = cmsStore.heroContainer;
  return Array.isArray(hero?.items) ? hero.items : [];
});

const activeHeroBanner = computed(() => {
  if (!heroBanners.value.length) return null;
  const safeIndex = heroIndex.value % heroBanners.value.length;
  return heroBanners.value[safeIndex] || heroBanners.value[0];
});

const announcementItems = computed(() => cmsStore.announcementItems || []);
const promoCarousels = computed(() => cmsStore.promoCarouselContainers || []);
const homeContainerConfig = computed(() => getOperatorCmsConfig(cmsStore.operator || APP_CONFIG.operator, 'home'));
const homeContainerDefinitions = computed(() => homeContainerConfig.value?.containers || []);
const homeStatusByKey = computed(() => cmsStore.containerStatus || {});
const isAnnouncementLoading = computed(() => Boolean(homeStatusByKey.value['announcements-main']?.loading));

const filterGamesByCategory = (games) => {
  if (activeCategory.value === 'all') return games;

  if (activeCategory.value === 'featured') {
    return games.filter((game) => game.featured);
  }

  if (activeCategory.value === 'slot') {
    return games.filter((game) => game.type === 'slot');
  }

  if (activeCategory.value === 'table') {
    return games.filter((game) => ['table', 'roulette', 'blackjack', 'baccarat', 'poker'].includes(game.type));
  }

  if (activeCategory.value === 'live') {
    return games.filter((game) => game.type.startsWith('live'));
  }

  return games;
};

const visibleLobbyRails = computed(() => {
  return (cmsStore.lobbyContainers || [])
    .map((lobby) => {
      const rawGames = Array.isArray(lobby?.games) ? lobby.games : [];
      const categoryGames = filterGamesByCategory(rawGames);
      const previewLimit = Number(lobby?.visualConfig?.previewLimit || 10);
      const safeLimit = Number.isFinite(previewLimit) && previewLimit > 0 ? previewLimit : 10;

      return {
        ...lobby,
        previewGames: categoryGames.slice(0, safeLimit),
      };
    })
    .filter((lobby) => lobby.previewGames.length > 0);
});

const featuredLobbyRail = computed(() => visibleLobbyRails.value[0] || null);
const remainingLobbyRails = computed(() => visibleLobbyRails.value.slice(1));
const pendingRemainingLobbyCount = computed(() => {
  const rendered = new Set(remainingLobbyRails.value.map((item) => item.code));
  const pendingDefinitions = homeContainerDefinitions.value
    .filter((definition) => definition.type === 'lobby' && definition.key !== 'lobby-featured')
    .filter((definition) => {
      const lobbyCode = String(definition.lobbyCode || '').trim();
      const isLoading = Boolean(homeStatusByKey.value[definition.key]?.loading);
      return isLoading && lobbyCode && !rendered.has(lobbyCode);
    });

  return pendingDefinitions.length;
});
const pendingPromoCarouselCount = computed(() => {
  return homeContainerDefinitions.value
    .filter((definition) => definition.type === 'carousel' && definition.key !== 'carousel-main')
    .filter((definition) => Boolean(homeStatusByKey.value[definition.key]?.loading))
    .length;
});

const stopHeroAutoplay = () => {
  if (heroAutoplayTimer) {
    clearInterval(heroAutoplayTimer);
    heroAutoplayTimer = null;
  }
};

const startHeroAutoplay = () => {
  stopHeroAutoplay();
  if (heroBanners.value.length <= 1) return;

  heroAutoplayTimer = setInterval(() => {
    heroIndex.value = (heroIndex.value + 1) % heroBanners.value.length;
  }, heroAutoplayIntervalMs);
};

const goToHero = (index) => {
  if (!heroBanners.value.length) return;
  heroIndex.value = index;
  startHeroAutoplay();
};

const prevHero = () => {
  if (!heroBanners.value.length) return;
  heroIndex.value = (heroIndex.value - 1 + heroBanners.value.length) % heroBanners.value.length;
  startHeroAutoplay();
};

const nextHero = () => {
  if (!heroBanners.value.length) return;
  heroIndex.value = (heroIndex.value + 1) % heroBanners.value.length;
  startHeroAutoplay();
};

const openBannerLink = (banner) => {
  if (!banner) return;

  if (banner.requiresAuth && !authStore.isAuthenticated) {
    handleLoginRequired();
    return;
  }

  const link = String(banner.link || '').trim();
  if (!link) return;

  const target = String(banner.target || '_self');
  if (link.startsWith('/')) {
    router.push(link);
    return;
  }

  window.open(link, target, 'noopener,noreferrer');
};

const scrollAnnouncements = (direction) => {
  if (!announcementScroller.value) return;
  announcementScroller.value.scrollBy({
    left: direction * 360,
    behavior: 'smooth',
  });
};

const heroImage = (banner) => banner?.imageDesktop || banner?.imageMobile || placeholderImage;

const formatAnnouncementDate = (announcement) => {
  const value = announcement?.startDate || announcement?.createdAt;
  if (!value) return t('home.update');

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return t('home.update');

  return parsed.toLocaleDateString(currentLanguageTag(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const handlePlay = (game) => {
  router.push(`/game/${game.id}`);
};

const handleLoginRequired = () => {
  if (openLogin) {
    openLogin();
    return;
  }
  router.push({ path: '/', query: { auth: 'login' } });
};

watch(heroBanners, (nextBanners) => {
  if (!nextBanners.length) {
    heroIndex.value = 0;
    stopHeroAutoplay();
    return;
  }

  if (heroIndex.value >= nextBanners.length) {
    heroIndex.value = 0;
  }

  startHeroAutoplay();
}, { immediate: true });

onMounted(async () => {
  await cmsStore.loadContent({ view: 'home' });
});

onBeforeUnmount(() => {
  stopHeroAutoplay();
});
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
